if (!customElements.get('kex-fbt')) {
  class KexFbt extends HTMLElement {
    connectedCallback() {
      this.loading = false;
      this.totalEl = this.querySelector('[data-kex-fbt-total]');
      this.addBtn = this.querySelector('[data-kex-fbt-add]');
      this.errorEl = this.querySelector('.kex-fbt__error');
      this.promoEl = this.querySelector('[data-kex-fbt-promo]');
      this.mainCheckbox = this.querySelector('[data-kex-fbt-main]');
      this.productVariantIds = (this.dataset.productVariants || '')
        .split(',')
        .map((id) => Number(id))
        .filter(Boolean);

      this.changeHandler = this.handleSelectionChange.bind(this);
      this.addHandler = this.addAllToCart.bind(this);
      this.variantChangeHandler = this.handleVariantChange.bind(this);
      this.recommendationsController = new AbortController();

      this.addEventListener('change', this.changeHandler);
      if (this.addBtn) this.addBtn.addEventListener('click', this.addHandler);
      document.addEventListener('on:variant:change', this.variantChangeHandler);

      this.refreshItems();
      this.updateTotal();
      this.loadRelatedRecommendations();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.changeHandler);
      if (this.addBtn) this.addBtn.removeEventListener('click', this.addHandler);
      document.removeEventListener('on:variant:change', this.variantChangeHandler);
      if (this.recommendationsController) this.recommendationsController.abort();
    }

    static formatMoney(cents) {
      const documentLocale = document.documentElement.lang || 'es-MX';
      const locale = documentLocale.toLowerCase().startsWith('es') ? 'es-MX' : documentLocale;
      const currency = window.Shopify && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : 'MXN';

      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
      }
    }

    static imageUrl(product, variant) {
      const variantImage = variant && variant.featured_image;
      const productImage = product && product.featured_image;
      const image = variantImage || productImage;

      if (typeof image === 'string') return image;
      if (image && typeof image === 'object') return image.url || image.src || '';
      return '';
    }

    static replaceTokens(template, replacements) {
      return Object.entries(replacements).reduce(
        (message, [token, value]) => message.split(`__${token}__`).join(String(value)),
        template || ''
      );
    }

    static comboId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
      return `kex-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    refreshItems() {
      this.items = Array.from(this.querySelectorAll('[data-kex-fbt-item]'));
      this.mainCheckbox = this.querySelector('[data-kex-fbt-main]');
    }

    checkedItems() {
      return this.items.filter((item) => (
        item.checked
        && !item.disabled
        && item.dataset.available !== 'false'
        && Number(item.dataset.variantId) > 0
      ));
    }

    discountFor(items = this.checkedItems()) {
      const main = items.find((item) => item.dataset.kexFbtRole === 'main');
      if (!main || main !== this.mainCheckbox || main.dataset.available === 'false') return null;

      const partners = items.filter((item) => item.dataset.kexFbtRole === 'partner');
      if (partners.length < 1 || partners.length > 2) return null;

      const comboItems = [main, ...partners];
      const uniqueVariantIds = new Set(comboItems.map((item) => item.dataset.variantId));
      if (uniqueVariantIds.size !== comboItems.length) return null;

      const isThreeProductCombo = comboItems.length === 3;
      const percent = Number(this.getAttribute(
        isThreeProductCombo ? 'data-discount-3-percent' : 'data-discount-2-percent'
      ));
      const code = this.getAttribute(isThreeProductCombo ? 'data-discount-3' : 'data-discount-2');
      if (!Number.isFinite(percent) || percent <= 0 || !code) return null;

      return { code, items: comboItems, percent };
    }

    setPromo(message) {
      if (!this.promoEl) return;
      this.promoEl.textContent = message || '';
      this.promoEl.hidden = !message;
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    setButtonState() {
      if (!this.addBtn) return;
      const disabled = this.loading || !this.discountFor();
      this.addBtn.disabled = disabled;
      this.addBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    updateTotal() {
      const checked = this.checkedItems();
      const total = checked.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);

      if (this.totalEl) {
        this.totalEl.dataset.total = total;
        this.totalEl.textContent = KexFbt.formatMoney(total);
      }

      if (!this.mainCheckbox || this.mainCheckbox.dataset.available === 'false') {
        this.setPromo(this.dataset.textSoldOut);
        this.setButtonState();
        return;
      }

      const discount = this.discountFor(checked);
      if (!discount) {
        this.setPromo(this.dataset.textSelectCombo);
        this.setButtonState();
        return;
      }

      const discountedTotal = Math.round(total * (100 - discount.percent) / 100);
      const template = discount.items.length === 3
        ? this.dataset.textPromoThree
        : this.dataset.textPromoTwo;
      this.setPromo(KexFbt.replaceTokens(template, {
        PRICE: KexFbt.formatMoney(discountedTotal)
      }));
      this.setButtonState();
    }

    handleSelectionChange(event) {
      if (!event.target.matches('[data-kex-fbt-item]')) return;
      this.setError('');
      this.updateTotal();
    }

    handleVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || !this.mainCheckbox) return;
      if (!this.productVariantIds.includes(Number(variant.id))) return;

      const available = variant.available !== false;
      this.mainCheckbox.dataset.variantId = String(variant.id);
      this.mainCheckbox.dataset.price = String(Number(variant.price || 0));
      this.mainCheckbox.dataset.available = available ? 'true' : 'false';
      this.mainCheckbox.disabled = !available;
      this.mainCheckbox.checked = available;

      const priceEl = this.querySelector('[data-kex-fbt-item-price]');
      if (priceEl) priceEl.textContent = KexFbt.formatMoney(variant.price);

      const row = this.mainCheckbox.closest('.kex-fbt__row');
      if (row) row.classList.toggle('is-disabled', !available);

      const mainThumb = this.querySelector('.kex-fbt__strip [data-kex-fbt-thumb]:not([data-kex-fbt-partner])');
      const variantImage = (variant.featured_image && variant.featured_image.src)
        || (variant.image && variant.image.url);
      if (mainThumb && variantImage) {
        const image = mainThumb.querySelector('img');
        if (image) {
          image.src = variantImage;
          image.alt = (variant.image && variant.image.alt) || image.alt;
          if (variant.image && variant.image.srcset) {
            image.srcset = variant.image.srcset;
          } else {
            image.removeAttribute('srcset');
          }
        }
        mainThumb.dataset.kexFbtThumb = String(variant.id);
      }

      this.setError('');
      this.updateTotal();
    }

    async loadRelatedRecommendations() {
      const url = this.dataset.recommendationsUrl;
      if (!url) {
        this.setAttribute('aria-busy', 'false');
        return;
      }

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: this.recommendationsController.signal
        });
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

        const payload = await response.json();
        const products = Array.isArray(payload.products) ? payload.products : [];
        const seenProducts = new Set([String(this.dataset.productId)]);
        const recommendations = [];

        for (const product of products) {
          if (!product || product.available === false || seenProducts.has(String(product.id))) continue;
          const variants = Array.isArray(product.variants) ? product.variants : [];
          const variant = variants.find((candidate) => candidate && candidate.available !== false && Number(candidate.id) > 0);
          if (!variant) continue;

          seenProducts.add(String(product.id));
          recommendations.push({ product, variant });
          if (recommendations.length === 2) break;
        }

        const currentPartnerCount = this.querySelectorAll('[data-kex-fbt-item][data-kex-fbt-role="partner"]').length;
        if (recommendations.length >= 2 || (currentPartnerCount === 0 && recommendations.length > 0)) {
          this.renderPartners(recommendations);
          this.dataset.recommendationSource = 'related';
          this.refreshItems();
          this.updateTotal();
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          // The server-rendered complementary products remain as a safe fallback.
        }
      } finally {
        this.setAttribute('aria-busy', 'false');
      }
    }

    renderPartners(recommendations) {
      const strip = this.querySelector('.kex-fbt__strip');
      const list = this.querySelector('.kex-fbt__list');
      if (!strip || !list) return;

      this.querySelectorAll('[data-kex-fbt-partner]').forEach((element) => element.remove());

      recommendations.forEach(({ product, variant }) => {
        const plus = document.createElement('span');
        plus.className = 'kex-fbt__plus';
        plus.dataset.kexFbtPartner = '';
        plus.dataset.productId = String(product.id);
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';

        const link = document.createElement('a');
        link.className = 'kex-fbt__thumb';
        link.href = product.url || '#';
        link.dataset.kexFbtPartner = '';
        link.dataset.kexFbtThumb = String(variant.id);
        link.dataset.productId = String(product.id);
        link.setAttribute('aria-label', product.title || '');

        const imageUrl = KexFbt.imageUrl(product, variant);
        if (imageUrl) {
          const image = document.createElement('img');
          image.className = 'kex-fbt__img';
          image.src = imageUrl;
          image.alt = product.title || '';
          image.loading = 'lazy';
          link.appendChild(image);
        }

        strip.append(plus, link);

        const row = document.createElement('li');
        row.className = 'kex-fbt__row';
        row.dataset.kexFbtPartner = '';
        row.dataset.productId = String(product.id);

        const label = document.createElement('label');
        label.className = 'kex-fbt__label';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'kex-fbt__checkbox';
        checkbox.checked = true;
        checkbox.dataset.kexFbtItem = '';
        checkbox.dataset.kexFbtRole = 'partner';
        checkbox.dataset.productId = String(product.id);
        checkbox.dataset.variantId = String(variant.id);
        checkbox.dataset.price = String(Number(variant.price || 0));
        checkbox.dataset.available = 'true';

        const title = document.createElement('span');
        title.className = 'kex-fbt__title';
        title.textContent = product.title || '';

        const price = document.createElement('span');
        price.className = 'kex-fbt__price';
        price.textContent = KexFbt.formatMoney(variant.price);

        label.append(checkbox, title, price);
        row.appendChild(label);
        list.appendChild(row);
      });

      document.dispatchEvent(new CustomEvent('kex:fbt:recommendations', {
        detail: {
          productId: String(this.dataset.productId),
          recommendationIds: recommendations.map(({ product }) => String(product.id))
        }
      }));
    }

    cartAddUrl() {
      if (window.theme && theme.routes && theme.routes.cartAdd) {
        return theme.routes.cartAdd.endsWith('.js') ? theme.routes.cartAdd : `${theme.routes.cartAdd}.js`;
      }
      const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
      return `${root}cart/add.js`;
    }

    cartUpdateUrl() {
      if (window.theme && theme.routes && theme.routes.cartUpdate) {
        return theme.routes.cartUpdate.endsWith('.js') ? theme.routes.cartUpdate : `${theme.routes.cartUpdate}.js`;
      }
      const root = window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
      return `${root}cart/update.js`;
    }

    async addAllToCart(event) {
      const discount = this.discountFor();
      if (!discount || this.loading) return;

      const button = event.currentTarget;
      const comboId = KexFbt.comboId();
      const items = discount.items.map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: 1,
        properties: {
          _kex_combo_id: comboId,
          _kex_combo_tier: String(discount.items.length),
          _kex_combo_code: discount.code
        }
      }));

      this.loading = true;
      this.setError('');
      button.classList.add('is-loading');
      this.setButtonState();

      try {
        const response = await fetch(this.cartAddUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: 'cart-icon-bubble',
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          const message = typeof data.description === 'string'
            ? data.description
            : (data.message || this.dataset.textAddError);
          throw new Error(message);
        }

        if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        let discountApplied = false;
        let appliedPercent = discount.percent;
        let displayRefreshed = false;
        try {
          if (window.KexTieredDiscount) {
            const tierResult = await window.KexTieredDiscount.activate();
            discountApplied = tierResult?.applied === true;
            appliedPercent = Number(tierResult?.percent || discount.percent);
            displayRefreshed = tierResult?.displayRefreshed === true;
          } else {
            const discountResponse = await fetch(this.cartUpdateUrl(), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              credentials: 'same-origin',
              body: JSON.stringify({ discount: discount.code })
            });
            discountApplied = discountResponse.ok;
          }
        } catch (discountError) {
          discountApplied = false;
        }

        const drawer = document.querySelector('cart-drawer');
        if (!displayRefreshed && drawer && typeof drawer.refreshCartDrawer === 'function') {
          try {
            await drawer.refreshCartDrawer();
          } catch (drawerError) {
            // Products and discount are already applied; a stale drawer must not report a false cart error.
          }
        }
        if (window.KexTieredDiscount) window.KexTieredDiscount.renderStatus();
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          detail: { opener: button }
        }));

        this.setPromo(discountApplied
          ? KexFbt.replaceTokens(this.dataset.textDiscountConfirmed, { PERCENT: appliedPercent })
          : this.dataset.textDiscountPending);
      } catch (error) {
        this.setError(error.message || this.dataset.textAddError);
      } finally {
        this.loading = false;
        button.classList.remove('is-loading');
        this.setButtonState();
      }
    }
  }

  customElements.define('kex-fbt', KexFbt);
}
